import {
  colors,
  ImpButton,
  ImpContainer,
  ImpFlex,
  ImpOtpField,
  ImpPad,
  ImpSpacer,
  ImpText,
  useSnackbar,
} from '@imprint-payments/imprint-ui';
import { useEffect, useState } from 'react';

import { EVENT } from '../analytics/analyticsConsts';
import { useCreatePin } from '../api/pin/createPin';
import { useImpState } from '../app/hooks';
import { SCREEN, setScreen } from '../app/screenStateSlice';
import { HeaderHandler } from '../components/HeaderHandler/HeaderHandler';
import { ANALYTICS } from '../firebase/firebase';
import { PAGE } from '../utils/routeConstant';

enum Step {
  CreatePIN,
  ConfirmPIN,
}

enum ScreenStatus {
  Clear,
  InvalidPIN,
  PinsDontMatch,
}

export function CreatePINPage() {
  const [createInput, setCreateInput] = useState('');
  const { language, navigate, dispatch, consumerInfo } = useImpState();
  const [step, setStep] = useState<Step>(Step.CreatePIN);
  const [screenStatus, setScreenStatus] = useState<ScreenStatus>(
    ScreenStatus.Clear
  );
  const [clear, setClear] = useState(false);
  const createPINMutation = useCreatePin();
  const { displayTemporarySnackbar } = useSnackbar();

  usePostAcceptPage();

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.createPinViewed);
  }, []);

  useEffect(() => {
    setScreenStatus(ScreenStatus.Clear);
    if (step === Step.CreatePIN) {
      setCreateInput('');
      setClear(true);
    }

    if (step === Step.ConfirmPIN) {
      setClear(true);
    }
  }, [step]);

  return (
    <div>
      <HeaderHandler />
      <ImpContainer>
        <ImpFlex fluid direction="column" align="center">
          <ImpContainer width="100%" maxWidth="520px">
            <ImpPad fluid padding="0px 16px">
              <ImpFlex fluid direction="column" align="center">
                <ImpSpacer height="2xl" />
                <ImpText typography="headline2">{getHeaderText(step)}</ImpText>
                {step === Step.CreatePIN && (
                  <>
                    <ImpSpacer height="xs" />
                    <ImpText
                      typography="body2"
                      color={colors.content.onSurfaceVariant}
                    >
                      {language.createPinSubtitle}
                    </ImpText>
                  </>
                )}
                <ImpSpacer height="xl" />

                <ImpOtpField
                  numInputs={4}
                  onFull={handleFullOTP}
                  mask
                  clearWithError={[
                    ScreenStatus.InvalidPIN,
                    ScreenStatus.PinsDontMatch,
                  ].includes(screenStatus)}
                  onChange={(val) => {
                    if (val.length > 0) {
                      setClear(false);
                      setScreenStatus(ScreenStatus.Clear);
                    }
                  }}
                  clear={clear}
                />
                <ImpSpacer height="xl" />

                {screenStatus === ScreenStatus.PinsDontMatch && (
                  <>
                    <ImpSpacer height="l" />
                    <ImpText
                      color={colors.content.onSurfaceError}
                      typography="body2"
                    >
                      {language.makeSurePinsMatch}
                    </ImpText>
                    <ImpSpacer height="l" />
                  </>
                )}

                {screenStatus === ScreenStatus.InvalidPIN && (
                  <>
                    <ImpSpacer height="l" />
                    <ImpText
                      color={colors.content.onSurfaceError}
                      typography="body2"
                    >
                      {language.invalidPin}
                    </ImpText>
                    <ImpSpacer height="l" />
                  </>
                )}

                {step === Step.CreatePIN && (
                  <>
                    <ImpText
                      color={
                        screenStatus === ScreenStatus.InvalidPIN
                          ? colors.content.onSurfaceError
                          : colors.content.onSurfaceVariant
                      }
                      typography="body3"
                    >
                      {language.pinCreationNote}
                    </ImpText>
                  </>
                )}

                {step === Step.ConfirmPIN && (
                  <>
                    <ImpButton
                      variant="secondary"
                      onClick={() => {
                        setCreateInput('');
                        setStep(Step.CreatePIN);
                      }}
                    >
                      {language.startOver}
                    </ImpButton>
                  </>
                )}
              </ImpFlex>
            </ImpPad>
          </ImpContainer>
        </ImpFlex>
      </ImpContainer>
    </div>
  );

  function handleFullOTP(pin: string) {
    if (step === Step.CreatePIN) {
      if (isInvalidPin(pin)) {
        setScreenStatus(ScreenStatus.InvalidPIN);
        return;
      }

      setCreateInput(pin);
      setStep(Step.ConfirmPIN);
      return;
    }

    if (pin !== createInput) {
      setScreenStatus(ScreenStatus.PinsDontMatch);
      return;
    }

    createPINMutation.mutate(
      {
        consumerUUID: consumerInfo.consumerUUID,
        newPin: pin,
        oldPin: createInput,
      },
      { onSuccess: handleCreatePINSuccess, onError: handlePINFail }
    );
  }

  function handleCreatePINSuccess() {
    dispatch(setScreen(SCREEN.COMPLETE_ON_PHONE));
    navigate(PAGE.COMPLETE_ON_PHONE);
  }

  function handlePINFail() {
    ANALYTICS.logEvent(EVENT.createPinFailed);
    setCreateInput('');
    setStep(Step.CreatePIN);
    setClear(true);
    displayTemporarySnackbar({ text: language.somethingWentWrong });
  }

  function getHeaderText(step: Step) {
    if (step === Step.CreatePIN) {
      return <div>{language.createAPIN}</div>;
    }
    return <div>{language.reEnterYourPIN}</div>;
  }
}

export function usePostAcceptPage() {
  const { navigate, consumerInfo } = useImpState();

  useEffect(() => {
    if (!consumerInfo?.auth?.token) {
      navigate(PAGE.HOME);
    }
  }, [consumerInfo, navigate]);
}

function isInvalidPin(pin: string) {
  // if same digit repeated 4 times
  if (pin.match(/(.)\1{3}/)) {
    return true;
  }

  // if pin is in sequence
  const seq = '01234567890';
  if (seq.includes(pin)) {
    return true;
  }
}
